import { MultiSelectFilter } from './MultiSelectFilter';
import { cascadeOptions, isFilterSelected, newDisabler } from '@utils/filters';
import { useMemo } from 'react';
import { differenceObjects } from '@utils/data';
import { useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';

export type OrgFilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

export enum OrgFilterOption {
    Company = 'companies',
    Reseller = 'resellers',
    Location = 'locations',
}

interface OrgFiltersProps {
    setOrgFilters: (option: string, value: string, selectOne?: boolean) => void;
    orgFilters: OrgFilterInput;
    showLocations?: boolean;
    disabled?: boolean;
}

const shouldDisable = newDisabler([OrgFilterOption.Company, OrgFilterOption.Reseller, OrgFilterOption.Location]);

export const OrgFilters = ({ setOrgFilters, orgFilters, showLocations = true, disabled = false }: OrgFiltersProps) => {
    const { data: { companyLocationMap = {}, companyResellerMap = {}, resellerLocationMap = {} } = {} } =
        useOrgMapQuery({});
    const { data: { companies = {}, resellers = {}, locations = {} } = {} } = useNameMapQuery({});

    const companyValues = useMemo(
        () => Object.entries(companies).map(([key, value]) => ({ companyId: key, companyName: value })),
        [companies]
    );

    /** This could have less iteration/traversal.  */
    const resellerValues = useMemo(() => {
        const options = Object.entries(differenceObjects(companies, resellers)).map(([key, value]) => ({
            resellerId: key,
            resellerName: value,
        }));

        return cascadeOptions({
            options,
            parent: orgFilters.companies,
            map: companyResellerMap,
            idKey: 'resellerId',
        });
    }, [resellers, orgFilters.companies]);

    const locationValues = useMemo(() => {
        const byReseller = isFilterSelected(orgFilters, OrgFilterOption.Reseller);

        const options = Object.entries(locations).map(([key, value]) => ({
            locationId: key,
            locationName: `#${key} - ${value}`,
        }));

        return cascadeOptions({
            options,
            parent: byReseller ? orgFilters.resellers : orgFilters.companies,
            map: byReseller ? resellerLocationMap : companyLocationMap,
            idKey: 'locationId',
        });
    }, [locations, orgFilters.resellers, orgFilters.companies]);

    return (
        <>
            <MultiSelectFilter
                search
                disabled={disabled || shouldDisable(OrgFilterOption.Company, orgFilters)}
                idKey={'companyId'}
                label={'Company'}
                options={companyValues}
                value={orgFilters.companies}
                onChange={(value) => setOrgFilters(OrgFilterOption.Company, value)}
                getLabelValue={(option) => option.companyName}
            />

            <MultiSelectFilter
                search
                disabled={disabled || shouldDisable(OrgFilterOption.Reseller, orgFilters)}
                idKey={'resellerId'}
                label={'Reseller'}
                options={resellerValues}
                value={orgFilters.resellers}
                onChange={(value) => setOrgFilters(OrgFilterOption.Reseller, value)}
                getLabelValue={(option) => option.resellerName}
            />

            {showLocations && (
                <MultiSelectFilter
                    search
                    disabled={disabled || shouldDisable(OrgFilterOption.Location, orgFilters)}
                    idKey={'locationId'}
                    label={'Location'}
                    options={locationValues}
                    value={orgFilters.locations}
                    onChange={(value) => setOrgFilters(OrgFilterOption.Location, value)}
                    getLabelValue={(option) => option.locationName}
                />
            )}
        </>
    );
};
